import { useState } from 'react';
import { E164, type Country } from '@acs/shared';
import { Banner, Button, Field, Input, Modal, Textarea } from '@acs/ui';
import { DuplicateClientError } from '../../shared/api/clients.js';
import { useCreateClient } from './hooks.js';
import { DIAL_PREFIX, normalizePhone } from './phoneInput.js';
import { useI18n } from '../../i18n/index.js';

export function AddClientDialog({
  businessId,
  country,
  onClose,
  onOpenExisting,
}: {
  businessId: string;
  country: Country;
  onClose: () => void;
  /** Navigate to an existing client's profile (duplicate-phone path). */
  onOpenExisting: (e164: string) => void;
}) {
  const { t } = useI18n();
  const create = useCreateClient(businessId);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState(DIAL_PREFIX[country] ?? '+');
  const [notes, setNotes] = useState('');
  const [duplicate, setDuplicate] = useState<string | null>(null);

  const normalized = normalizePhone(phone);
  const phoneValid = E164.safeParse(normalized).success;
  const nameValid = name.trim().length >= 1 && name.trim().length <= 120;
  const canSubmit = phoneValid && nameValid && !create.isPending;

  const phoneError = phone.trim() && !phoneValid
    ? t('addClient.phoneHelp')
    : undefined;

  const submit = async () => {
    setDuplicate(null);
    try {
      await create.mutateAsync({ phone: normalized, name: name.trim(), notes: notes.trim() });
      onClose();
    } catch (err) {
      if (err instanceof DuplicateClientError) setDuplicate(normalized);
      // Other errors keep the dialog open with values intact (create.isError).
    }
  };

  return (
    <Modal
      closeLabel={t('ui.closeDialog')}
      title={t('addClient.title')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button disabled={!canSubmit} loading={create.isPending} onClick={() => void submit()}>
            {t('clients.add')}
          </Button>
        </>
      }
    >
      {/* No grid gap here: `.tc-field` now carries its own bottom spacing, and a
          container gap on top of it would space these fields wider than every
          other form in the app. */}
      <div>
        <Field label={t('clients.name')} required>
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('addClient.namePlaceholder')}
            maxLength={120}
          />
        </Field>

        <Field
          label={t('addClient.phone')}
          required
          hint={t('addClient.phoneHint')}
          error={phoneError}
        >
          <Input
            mono
            value={phone}
            onChange={(e) => { setPhone(e.target.value); setDuplicate(null); }}
            placeholder={t('addClient.phonePlaceholder')}
            inputMode="tel"
          />
        </Field>

        {duplicate && (
          <Banner
            tone="warning"
            actions={
              <Button size="sm" variant="secondary" onClick={() => onOpenExisting(duplicate)}>
                {t('addClient.openProfile')}
              </Button>
            }
          >
            {t('addClient.duplicate')}
          </Banner>
        )}
        {create.isError && !duplicate && (
          <Banner tone="danger">{t('addClient.failed')}</Banner>
        )}

        <Field label={t('clients.notes')} optional>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            maxLength={2000}
            placeholder={t('addClient.notesPlaceholder')}
          />
        </Field>
      </div>
    </Modal>
  );
}
